import constants from '../../utils/constants/endpoints';
import { v4 as uuidv4 } from 'uuid';
import { selectCurrentWorkspaceId } from 'slices/workspacesSlice';
import { updateProcess } from './updateProcess';
import {
  getStoredUpdateTokens,
  removeStoredUpdateTokens,
} from './storage';
import {
  getUserLocalStorage,
  getDirectTokenLocalStorage,
} from '../../utils/helpers';
import envService from 'src/services/app/envService';
import { selectShouldSync as selectShouldSyncTags } from 'slices/tagsBoardSlice';
import { selectShouldSync as selectShouldSyncBoard } from 'slices/boardSlice/slice';
import type { AppStore } from '../store/store';
import { container } from '../../app/ioc/container';
import { TYPES } from '../../app/ioc/types';
import type { IPersistentRepository } from '../../services/PersistentRepository';
import { TYPES as SYNC_MODULE_TYPES } from '../../features2/sync/di/types';
import type { ISyncStateRepository } from '../../features2/sync/domain/abstractions/ISyncStateRepository';
import type { UpdateTokensMap } from './types';
import { PERSONAL_SCOPE } from './entities';
import { getSseReconnectDelay } from './sseReconnect';

export const streamUpdatesManagerContext: { instance: StreamUpdatesManager | null } = {
  instance: null,
};

const logPrerelease = (...args: unknown[]) => {
  if (envService.isPrerelease()) {
    // eslint-disable-next-line no-console
    console.warn('[streamUpdatesV3]', ...args);
  }
};

const getDirectToken = () => {
  const directToken = getDirectTokenLocalStorage();

  if (directToken) {
    return directToken;
  }

  const userData = getUserLocalStorage();

  return userData?.idToken;
};

const ensureTokenScopes = (
  source: UpdateTokensMap,
  scopeKeys: string[]
): UpdateTokensMap => {
  const result: UpdateTokensMap = {};

  scopeKeys.forEach((scope) => {
    result[scope] = { ...(source?.[scope] || {}) };
  });

  return result;
};

export default class StreamUpdatesManager {
  private store: AppStore;
  private sse: EventSource | null;
  private sseReconnectAttempt = 0;
  private sseReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  public id: string;
  public isClosed: boolean;
  private tokens: UpdateTokensMap;

  constructor(store: AppStore) {
    this.store = store;
    this.id = uuidv4();
    this.isClosed = false;
    this.tokens = getStoredUpdateTokens() || {};
    this.sse = null;
    streamUpdatesManagerContext.instance = this;
    logPrerelease('init tokens', this.tokens);
  }

  async startWithoutUpdate() {
    const syncStateRepository = container.get<ISyncStateRepository>(
      SYNC_MODULE_TYPES.SyncStateRepository
    );
    const persistentRepository = container.get<IPersistentRepository>(TYPES.PersistentRepository);

    const state = this.store.getState();
    const currentWorkspaceId = selectCurrentWorkspaceId(state);
    syncStateRepository.restoreStatus.next('restored');
    persistentRepository.setWorkspaceRestored(currentWorkspaceId, true);
  }

  async start() {
    const syncStateRepository = container.get<ISyncStateRepository>(
      SYNC_MODULE_TYPES.SyncStateRepository
    );
    const persistentRepository = container.get<IPersistentRepository>(TYPES.PersistentRepository);

    const updatingWorkspaceIds: string[] = [];

    const resultToken = getDirectToken();
    if (!resultToken) {
      logPrerelease('start aborted: missing token');
      return;
    }

    const state = this.store.getState();
    const currentWorkspaceId = selectCurrentWorkspaceId(state);

    const scopesToSync = [PERSONAL_SCOPE];
    if (currentWorkspaceId) {
      scopesToSync.push(currentWorkspaceId);
    }

    const tokensToProcess = ensureTokenScopes(this.tokens, scopesToSync);
    logPrerelease('start sync scopes', scopesToSync, 'tokensToProcess', tokensToProcess);

    const result = await updateProcess({
      tokens: tokensToProcess,
      store: this.store,
      manager: this,
    });

    if (result.status === 'aborted') {
      logPrerelease('initial sync aborted');
      return;
    }

    this.tokens = result.tokens;

    if (
      syncStateRepository.restoreStatus.value !== 'restored' &&
      result.status !== 'error'
    ) {
      syncStateRepository.restoreStatus.next('restored');
      if (currentWorkspaceId) {
        persistentRepository.setWorkspaceRestored(currentWorkspaceId, true);
      }
    }

    if (result.status === 'error') {
      logPrerelease('initial sync error; tokens not persisted', result.tokens);
      return;
    }

    const handleMessage = async (event: MessageEvent) => {
      const shouldSyncTags = selectShouldSyncTags(this.store.getState());
      const shouldSyncBoard = selectShouldSyncBoard(this.store.getState());

      if (shouldSyncTags || shouldSyncBoard || this.isClosed) {
        return;
      }

      try {
        const parsed = JSON.parse(event.data || '{}');
        const { workspace_id: workspaceIdRaw, type } = parsed;
        const workspaceId = workspaceIdRaw || PERSONAL_SCOPE;
        const state = this.store.getState();
        const currentWorkspaceId = selectCurrentWorkspaceId(state);

        const scopeTokens = getStoredUpdateTokens() || {};

        if (workspaceIdRaw === undefined && type?.includes('contact')) {
          logPrerelease('SSE contact event without workspace_id', { parsed });
        }
        const shouldUpdate =
          workspaceId === PERSONAL_SCOPE || workspaceId === currentWorkspaceId;

        if (!shouldUpdate) {
          logPrerelease('skip workspace sync: workspace mismatch', {
            eventWorkspaceId: workspaceId,
            currentWorkspaceId,
          });
          return;
        }

        if (updatingWorkspaceIds.includes(workspaceId)) {
          return;
        }

        const existingTokens = scopeTokens[workspaceId];

        if (!existingTokens) {
          logPrerelease('skip workspace sync: missing tokens for scope', {
            workspaceId,
            scopeKeys: Object.keys(scopeTokens || {}),
          });
          return;
        }

        updatingWorkspaceIds.push(workspaceId);

        const result = await updateProcess({
          tokens: { [workspaceId]: existingTokens },
          store: this.store,
          manager: this,
        });

        if (result.status !== 'aborted') {
          this.tokens = result.tokens;
        }

        updatingWorkspaceIds.splice(updatingWorkspaceIds.indexOf(workspaceId), 1);
      } catch (error) {
        console.warn('SSE data parsing error!', error);
      }
    };

    this.openSse(resultToken, handleMessage);
  }

  private openSse(token: string, onMessage: (event: MessageEvent) => void) {
    const sse = new EventSource(
      `${process.env.NEXT_PUBLIC_API_URL}${constants.stream.sse}?token=${token}`
    );
    this.sse = sse;

    sse.onopen = () => {
      // Events published while the connection was down are not replayed.
      if (this.sseReconnectAttempt > 0) {
        this.sseReconnectAttempt = 0;
        this.forceGetStreamUpdates();
      }
    };

    sse.onmessage = onMessage;

    sse.onerror = (error) => {
      console.warn(error);
      sse.close();

      if (this.isClosed || this.sse !== sse) {
        return;
      }

      const delay = getSseReconnectDelay(this.sseReconnectAttempt);
      this.sseReconnectAttempt += 1;
      this.sseReconnectTimer = setTimeout(() => {
        const currentToken = getDirectToken();

        if (this.isClosed || !currentToken) {
          return;
        }

        this.openSse(currentToken, onMessage);
      }, delay);
    };
  }

  forceGetStreamUpdates = () => {
    const storedTokens = getStoredUpdateTokens();

    if (!storedTokens) {
      return;
    }

    const state = this.store.getState();
    const currentWorkspaceId = selectCurrentWorkspaceId(state);

    const tokensToProcess: UpdateTokensMap = {};

    if (storedTokens[PERSONAL_SCOPE]) {
      tokensToProcess[PERSONAL_SCOPE] = storedTokens[PERSONAL_SCOPE];
    }

    if (currentWorkspaceId && storedTokens[currentWorkspaceId]) {
      tokensToProcess[currentWorkspaceId] = storedTokens[currentWorkspaceId];
    }

    if (Object.keys(tokensToProcess).length === 0) {
      return;
    }

    updateProcess({
      tokens: tokensToProcess,
      store: this.store,
      manager: this,
    }).then((result) => {
      if (result.status !== 'aborted') {
        this.tokens = result.tokens;
      }
    });
  };

  fullResync = async () => {
    const persistentRepository = container.get<IPersistentRepository>(TYPES.PersistentRepository);
    const syncStateRepository = container.get<ISyncStateRepository>(
      SYNC_MODULE_TYPES.SyncStateRepository
    );
    const state = this.store.getState();
    const currentWorkspaceId = selectCurrentWorkspaceId(state);

    if (currentWorkspaceId) {
      persistentRepository.setWorkspaceRestored(currentWorkspaceId, false);
    }
    syncStateRepository.restoreStatus.next('isRestoring');

    removeStoredUpdateTokens();
    this.tokens = {};

    const scopesToSync = [PERSONAL_SCOPE];
    if (currentWorkspaceId) {
      scopesToSync.push(currentWorkspaceId);
    }

    const tokensToProcess = ensureTokenScopes({}, scopesToSync);

    const result = await updateProcess({
      tokens: tokensToProcess,
      store: this.store,
      manager: this,
    });

    if (result.status !== 'aborted') {
      this.tokens = result.tokens;
    }

    if (syncStateRepository.restoreStatus.value !== 'restored' && result.status !== 'error') {
      syncStateRepository.restoreStatus.next('restored');
      if (currentWorkspaceId) {
        persistentRepository.setWorkspaceRestored(currentWorkspaceId, true);
      }
    } else {
      syncStateRepository.restoreStatus.next('idle');
    }
  };

  destroy = () => {
    logPrerelease('destroy called; closing stream manager', new Error('destroy stack'));
    this.isClosed = true;
    streamUpdatesManagerContext.instance = null;
    if (this.sseReconnectTimer) {
      clearTimeout(this.sseReconnectTimer);
      this.sseReconnectTimer = null;
    }
    if (this.sse) {
      logPrerelease('closing SSE connection');
      this.sse.close();
    }
  };
}
